import { Box, Timeline } from "@primer/react";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { crmHocSinhApi } from "../../../api/crmHocSinhApi";
import { crmLopApi } from "../../../api/crmLopApi";
import { trangThaiHocSinhApi } from "../../../api/trangThaiHocSinhApi";
import { truongApi } from "../../../api/truongApi";
import DataTable from "../../../components/ui/data-table";
import Text from "../../../components/ui/text";
import { RootState } from "../../../state/reducers";
import { PlaceHolder } from "../../../components/place-holder";

interface LichSuHocProps {
  hocSinh: any;
}

const LichSuHoc: React.FC<LichSuHocProps> = ({ hocSinh }) => {
  const [history, setHistory] = useState<any[]>([]);
  const [lichSuDoiMa, setLichSuDoiMa] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [lops, setLops] = useState<any[]>([]);
  const [trangThaiHocSinhs, setTrangThaiHocSinhs] = useState<any[]>([]);
  const [truongs, setTruongs] = useState<any[]>([]);

  const { dm_khois, dm_hes, dm_cosos } = useSelector(
    (state: RootState) => state.categorySource
  );

  useEffect(() => {
    const fetchData = async () => {
      if (hocSinh?.id > 0) {
        setLoading(true);
        try {
          // Fetch history
          const res = await crmHocSinhApi.getAllHSCCById(hocSinh.id);
          if (res.is_success && res.data) {
            const sorted = res.data.sort((a: any, b: any) =>
              b.nam_hoc.localeCompare(a.nam_hoc)
            );
            setHistory(sorted);
          }

          // Fetch lops
          const lopsRes = await crmLopApi.selectAll();
          if (lopsRes.is_success && lopsRes.data) {
            setLops(lopsRes.data);
          }

          // Fetch trang thai hoc sinh
          const trangThaiRes = await trangThaiHocSinhApi.selectAll();
          if (trangThaiRes.is_success && trangThaiRes.data) {
            setTrangThaiHocSinhs(trangThaiRes.data);
          }

          // Fetch truongs
          const truongsRes = await truongApi.selectAll();
          if (truongsRes.is_success && truongsRes.data) {
            setTruongs(truongsRes.data);
          }

          // Fetch lich su doi ma
          const lichSuDoiMaRes = await crmHocSinhApi.getLichSuDoiMa(hocSinh.id);
          if (lichSuDoiMaRes.is_success && lichSuDoiMaRes.data) {
            setLichSuDoiMa(lichSuDoiMaRes.data);
          }
        } catch (error) {
          console.error("Error fetching Lich Su Hoc", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [hocSinh.id]);

  const getCoSoName = (id: number) =>
    dm_cosos.find((x: any) => x.id === id)?.ten_co_so || "";
  const getTruongName = (id: number) =>
    truongs.find((x: any) => x.id === id)?.ten_truong || "";
  const getKhoiName = (id: number) =>
    dm_khois.find((x: any) => x.id === id)?.ten_khoi || "";
  const getHeName = (id: number) =>
    dm_hes.find((x: any) => x.id === id)?.ten_he || "";
  const getLopName = (id: number) =>
    lops.find((x: any) => x.id === id)?.ten_lop || "";
  const getTrangThaiHocSinhName = (id: number) =>
    trangThaiHocSinhs.find((x: any) => x.id === id)?.trang_thai || "";

  if (loading) {
    return <PlaceHolder />;
  }

  return (
    <Box p={4}>
      {/* Lịch sử học */}
      {history && history.length > 0 && (
        <>
          <Text text="Lịch sử học" sx={{ fontSize: 2, fontWeight: "bold", mb: 3, display: "block" }} />
          <Timeline>
            {history.map((item, index) => (
              <Timeline.Item key={index} sx={{ alignItems: "center", pb: "50px" }}>
                <Timeline.Badge sx={{
                  width: "max-content",
                  padding: "0 18px",
                  borderRadius: "12px",
                  backgroundColor: index === 0 ? "accent.emphasis" : "canvas.default",
                  color: index === 0 ? "fg.onEmphasis" : "fg.default",
                  fontWeight: index === 0 ? "bold" : "normal",
                  border: "1px solid",
                  borderColor: index === 0 ? "accent.emphasis" : "border.default"
                }}>
                  {item.nam_hoc}
                </Timeline.Badge>
                <Timeline.Body>
                  <Box sx={{ ml: 4, display: "flex", flexDirection: "column", gap: 2, width: "fit-content" }}>
                    {/* Row 1: Basic Info */}
                    <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Text text="Cơ sở" sx={{ fontSize: 0, color: "fg.muted" }} />
                        <Text text={getCoSoName(item.dm_coso_id) || (hocSinh.dm_coso_id ? getCoSoName(hocSinh.dm_coso_id) : "-")} sx={{ fontSize: 1, fontWeight: 500, color: "fg.default" }} />
                      </Box>

                      {getTruongName(item.dm_truong_id) && (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Text text="Trường" sx={{ fontSize: 0, color: "fg.muted" }} />
                          <Text text={getTruongName(item.dm_truong_id)} sx={{ fontSize: 1, fontWeight: 500, color: "fg.default" }} />
                        </Box>
                      )}

                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Text text="Khối/Hệ" sx={{ fontSize: 0, color: "fg.muted" }} />
                        <Text text={`${getKhoiName(item.dm_khoi_id) || "-"} / ${getHeName(item.dm_he_id) || "-"}`} sx={{ fontSize: 1, fontWeight: 500, color: "fg.default" }} />
                      </Box>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Text text="Lớp" sx={{ fontSize: 0, color: "fg.muted" }} />
                        <Text text={getLopName(item.dm_lop_id) || "-"} sx={{ fontSize: 1, fontWeight: "bold", color: "fg.default" }} />
                      </Box>
                    </Box>

                    {/* Row 2: Status */}
                    {getTrangThaiHocSinhName(item.dm_trangthaihocsinh_id) && (
                      <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", pt: 2, borderTop: "1px solid", borderColor: "border.subtle" }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Text text="Trạng thái" sx={{ fontSize: 0, color: "fg.muted" }} />
                          <Text text={getTrangThaiHocSinhName(item.dm_trangthaihocsinh_id)} sx={{ fontWeight: "bold", fontSize: 1, color: "fg.default" }} />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Timeline.Body>
              </Timeline.Item>
            ))}
          </Timeline>
        </>
      )}

      {/* Lịch sử đổi mã */}
      <Box sx={{ mt: history.length > 0 ? 4 : 0 }}>
        <Text text="Lịch sử đổi mã" sx={{ fontSize: 2, fontWeight: "bold", mb: 3, display: "block" }} />
        <DataTable
          height="200px"
          columns={[
            {
              dataField: "ma_hs",
              width: "auto",
              caption: "Mã học sinh",
              cellRender: (data: any) => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  {data.ma_hs || "-"}
                </Box>
              ),
            },
            {
              dataField: "ma_tn",
              width: "auto",
              caption: "Mã TN",
              cellRender: (data: any) => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  {data.ma_tn || "-"}
                </Box>
              ),
            },
            {
              dataField: "ma_cu",
              width: "auto",
              caption: "Mã cũ",
              cellRender: (data: any) => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  {data.ma_cu || "-"}
                </Box>
              ),
            },
            {
              dataField: "created_time",
              width: "auto",
              caption: "Thời gian",
              cellRender: (data: any) => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  {data.created_time ? moment(data.created_time).format("DD/MM/YYYY HH:mm") : ""}
                </Box>
              ),
            },
            {
              dataField: "nguoi_thay_doi",
              width: "auto",
              caption: "Người thay đổi",
              cellRender: (data: any) => (
                <Box sx={{ whiteSpace: "nowrap" }}>
                  {data.nguoi_thay_doi || ""}
                </Box>
              ),
            },
          ]}
          data={lichSuDoiMa}
        />
      </Box>

      {/* Không có dữ liệu */}
      {(!history || history.length === 0) && (!lichSuDoiMa || lichSuDoiMa.length === 0) && (
        <Text text="Chưa có dữ liệu lịch sử." />
      )}
    </Box>
  );
};

export default LichSuHoc;
